import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

export default class PDFUtils {
  captureFullPage = async (page: HTMLElement) => {
    if (!page) {
      throw new Error("Element not found");
    }

    // Store original dimensions
    const originalWidth = page.style.width;
    const originalHeight = page.style.height;

    // Set dimensions to scroll dimensions to make the entire content visible
    page.style.width = `${page.scrollWidth}px`;
    page.style.height = `${page.scrollHeight}px`;

    // Ensure all styles are applied before capturing
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Capture the canvas
    const data = await html2canvas(page, {
      scale: 3,
      useCORS: true,
      scrollX: 0,
      scrollY: 0,
      width: page.scrollWidth,
      height: page.scrollHeight,
      windowWidth: page.scrollWidth,
      windowHeight: page.scrollHeight,
    });

    // Revert the dimensions back to the original state
    page.style.width = originalWidth;
    page.style.height = originalHeight;

    // Get the image data URL
    // return data.toDataURL("image/png"), page.scrollWidth, page.scrollHeight;
    return data;
  };

  createNewImageFromExisting = async (
    imgSrc: any,
    offsetX: number,
    offsetY: number,
    targetWidth: number,
    targetHeight: number,
  ): Promise<string> => {
    // Create a new image element
    const img = new Image();
    img.src = imgSrc;

    // Wait for the image to load
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = (error) =>
        reject(new Error("Failed to load image: " + error));
    });

    // Create a canvas and set its dimensions
    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    // Get the canvas context
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Canvas context could not be obtained");
    }

    // Draw the image onto the canvas
    ctx.drawImage(img, offsetX, offsetY, targetWidth, targetHeight);

    // Return the data URL of the new image
    return canvas.toDataURL("image/png");
  };

  addPageInPDF = async (pdf: jsPDF, elem: HTMLElement, pageNo: number = 0) => {
    const mx = 20;
    const my = 20;

    const pageWidth = pdf.internal.pageSize.getWidth() - 2 * mx;
    const pageHeight = pdf.internal.pageSize.getHeight() - 2 * my;
    const data = await this.captureFullPage(elem as HTMLElement);
    const img = data.toDataURL("image/png");
    const imgProperties = pdf.getImageProperties(img);
    const aspectRatio = imgProperties.width / imgProperties.height;
    // let imgHeight = pageHeight
    let imgWidth = pageWidth;
    let imgHeight = imgWidth / aspectRatio;
    const numOfPages = Math.ceil(imgHeight / pageHeight);
    // pdf.addImage(img, "PNG", mx, my, imgWidth, imgHeight);

    for (let i = pageNo; i < numOfPages; i++) {
      if (i > 0) {
        pdf.addPage();
      }
      const newImg = await this.createNewImageFromExisting(
        img,
        pageWidth,
        pageHeight,
        mx,
        my - pageHeight * i,
      );
      pdf.addImage(newImg, "PNG", mx, my - pageHeight * i, imgWidth, imgHeight);
    }
  };
  async generatePDF(
    elem: HTMLElement | HTMLCanvasElement,
    paddingX: number,
    paddingY: number,
  ) {
    const data: HTMLCanvasElement =
      elem instanceof HTMLElement ? await this.captureFullPage(elem) : elem;

    const img = data.toDataURL("image/png");
    const imgWidth = data.width; //249-
    const imgHeight = data.height; //* imgWidth/data.width

    // Calculate PDF dimensions with padding
    const pdfWidth = imgWidth + 2 * paddingX;
    const pdfHeight = imgHeight + 2 * paddingY;

    // Create jsPDF instance with custom dimensions
    const pdf = new jsPDF({
      orientation: "p",
      unit: "px",
      format: [pdfWidth, pdfHeight],
    });

    // Add image to the PDF with padding
    pdf.addImage(img, "PNG", paddingX, paddingY, imgWidth, imgHeight);

    return pdf;
  }

  async generatePDFForA4(element: HTMLElement) {
    if (!element) {
      throw new Error("Element not found");
    }

    // High DPI rendering (KEY FIX)
    const canvas = await html2canvas(element, {
      scale: 3, // increase for sharper output (2–4 ideal)
      useCORS: true,
      scrollX: 0,
      scrollY: 0,
    });

    const imgData = canvas.toDataURL("image/png");

    // A4 dimensions in mm
    const pdf = new jsPDF({
      orientation: "p",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = pdf.internal.pageSize.getWidth(); // ~210mm
    const pageHeight = pdf.internal.pageSize.getHeight(); // ~297mm

    // Convert canvas px → mm ratio
    const pxToMm = pageWidth / canvas.width;
    const imgHeightMm = canvas.height * pxToMm;

    let heightLeft = imgHeightMm;
    let position = 0;

    // First page
    pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeightMm);
    heightLeft -= pageHeight;

    // Extra pages
    while (heightLeft > 0) {
      position = heightLeft - imgHeightMm;

      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeightMm);

      heightLeft -= pageHeight;
    }

    return pdf;
  }
}
